import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { LineItemUnit } from "@/apis/compare/types";

interface DlViewConfig {
  dlViewIdentityByNormalizedCellId: Map<string, string>;
  schemaTypeByNormalizedCellId: Map<string, string>;
  referenceBalance?: "DEBIT" | "CREDIT" | null;
  labelMap?: Map<string, string>;
  isTransposed: boolean;
  lineItemUnits?: Record<string, LineItemUnit>;
  /** DOM 선택/렌더링에 사용하는 섹션 ID */
  sectionId?: string;
  /** 원본 보고서 위치를 식별하는 섹션 ID */
  identitySectionId?: string;
  showPeriodLabel: boolean;
  unitDivisor?: number;
}

const DEFAULT_DL_VIEW_CONFIG: DlViewConfig = {
  dlViewIdentityByNormalizedCellId: new Map(),
  schemaTypeByNormalizedCellId: new Map(),
  isTransposed: false,
  showPeriodLabel: true,
};

const DlViewConfigContext = createContext<DlViewConfig>(DEFAULT_DL_VIEW_CONFIG);

export const DlViewConfigProvider = ({
  children,
  dlViewIdentityByNormalizedCellId,
  schemaTypeByNormalizedCellId,
  referenceBalance,
  labelMap,
  isTransposed,
  lineItemUnits,
  sectionId,
  identitySectionId,
  showPeriodLabel,
  unitDivisor,
}: DlViewConfig & { children: ReactNode }) => {
  const value = useMemo<DlViewConfig>(
    () => ({
      dlViewIdentityByNormalizedCellId,
      schemaTypeByNormalizedCellId,
      referenceBalance,
      labelMap,
      isTransposed,
      lineItemUnits,
      sectionId,
      identitySectionId,
      showPeriodLabel,
      unitDivisor,
    }),
    [
      dlViewIdentityByNormalizedCellId,
      schemaTypeByNormalizedCellId,
      referenceBalance,
      labelMap,
      isTransposed,
      lineItemUnits,
      sectionId,
      identitySectionId,
      showPeriodLabel,
      unitDivisor,
    ],
  );
  return (
    <DlViewConfigContext.Provider value={value}>
      {children}
    </DlViewConfigContext.Provider>
  );
};

export const useDlViewConfig = (): DlViewConfig =>
  useContext(DlViewConfigContext);
